import { useMainStore } from '@/store'; // adjust if needed
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import Dashboard from '../pages/Dashboard.vue';
import { setPageTitle, can } from '@/composables';
import { clearImpersonation, saveImpersonation } from '@/composables/impersonation';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/delivery-box',
    name: 'delivery-box',
    component: () => import('../pages/DeliveryBox.vue'),
  },
  {
    path: '/default-categories',
    name: 'default-categories',
    component: () => import('../pages/DefaultCategories.vue'),
  },
  {
    path: '/resellers-management',
    name: 'resellers-management',
    component: () => import('../pages//ResellersManagement/ResellersManagement.vue'),
  },
  {
    path: '/order-barcode',
    name: 'order-barcode',
    component: () => import('../pages/OrderBarcode.vue'),
  },
  {
    path: '/generate-drawing-sheet',
    name: 'generate-drawing-sheet',
    component: { template: '<div></div>' },
    beforeEnter() {
      window.location.href = '/generate-drawing-sheet/index.html';
    },
  },
  {
    path: '/buying-groups-sales-reps',
    name: 'buying-groups-sales-reps',
    component: () => import('@/pages/BuyingGroupSalesRep.vue')
  },
  // Settings pilot (AB#20755) — dark launch: routes registered while the
  // sidebar navigation still points at the legacy app. Route paths match
  // the legacy app exactly so deep links survive the cut-over.
  {
    path: '/general-settings',
    name: 'general-settings',
    component: () => import('@/pages/Settings/GeneralSettings.vue'),
  },
  {
    path: '/email-configuration',
    name: 'email-configuration',
    component: () => import('@/pages/Settings/EmailConfiguration.vue'),
  },
  {
    path: '/ui-branding',
    name: 'ui-branding',
    component: () => import('@/pages/Settings/UIBranding.vue'),
  },
  // Price List, SKU & Factories (AB#20756) — dark launch, same discipline as the
  // Settings pilot: routes live here, the sidebar keeps pointing at the legacy
  // app until each path is added to V2_OWNED_PATHS in useMenuHref.ts.
  {
    path: '/price-list',
    name: 'price-list',
    component: () => import('@/pages/PriceList/PriceList.vue'),
  },
  {
    path: '/factories',
    name: 'factories',
    component: () => import('@/pages/Factories/Factories.vue'),
  },
  {
    path: '/sku',
    name: 'sku',
    component: () => import('@/pages/Sku/Sku.vue'),
  },
  // Categories, Design Categories & Brand (AB#20757) — dark launch. Paths match
  // the legacy admin and the live navigation url_path values (/brands,
  // /design-categories); /products/categories is direct-URL only, as in legacy.
  {
    path: '/products/categories',
    name: 'categories',
    component: () => import('@/pages/Categories/Categories.vue'),
  },
  {
    path: '/design-categories',
    name: 'design-categories',
    component: () => import('@/pages/DesignCategories/DesignCategories.vue'),
  },
  {
    path: '/brands',
    name: 'brands',
    component: () => import('@/pages/Brands/Brands.vue'),
  },
  // Translations, Email & Video Templates (AB#20758) — dark launch. Paths match the
  // legacy admin and the navigation url_path values; sidebar cut-over follows QA.
  {
    path: '/translations',
    name: 'translations',
    component: () => import('@/pages/Translations/Translations.vue'),
  },
  {
    path: '/email-templates',
    name: 'email-templates',
    component: () => import('@/pages/EmailTemplates/EmailTemplates.vue'),
  },
  {
    path: '/general-action',
    name: 'general-action',
    component: () => import('@/pages/GeneralAction/GeneralAction.vue'),
  },
  {
    path: '/video-templates',
    name: 'video-templates',
    component: () => import('@/pages/VideoTemplates/VideoTemplates.vue'),
    props: { kind: 'company' },
  },
  {
    path: '/default-video-templates',
    name: 'default-video-templates',
    component: () => import('@/pages/VideoTemplates/VideoTemplates.vue'),
    props: { kind: 'default' },
  },
  {
    path: '/video-usage',
    name: 'video-usage',
    component: () => import('@/pages/VideoUsage/VideoUsage.vue'),
  },
  {
    path: '/products/data-source',
    name: 'data-source',
    component: () => import('@/pages/DataSource/DataSource.vue'),
  },
  {
    // Tracking Master Dashboard (US #18826, task 18973). Sidebar visibility is
    // role-gated (Logistics/CSM) in the store; this route is lazy-loaded.
    path: '/tracking',
    name: 'tracking',
    component: () => import('../pages/TrackingMaster.vue'),
    // Role-gated (task 18973): the beforeEach guard 403s direct-URL access
    // unless the user's role carries the view-tracking-dashboard permission.
    meta: { permission: 'view-tracking-dashboard' },
  },
  {
    // 403 shown when a user hits a permission-restricted page they can't access
    // (task 18973). Reachable regardless of permission.
    path: '/403',
    name: 'not-authorized',
    component: () => import('@/pages/NotAuthorized.vue'),
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/pages/Roles/Roles.vue'),
  },
  {
    path: '/permissions',
    name: 'permissions',
    component: () => import('@/pages/Permissions/Permissions.vue'),
  },
  {
    path: '/companies',
    name: 'companies',
    component: () => import('@/pages/Companies/Companies.vue'),
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/pages/Users/Users.vue'),
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/pages/Customers/Customers.vue'),
  },
  {
    // Standalone order-type change page (US #20569). Its own URL so an Order
    // Administrator can be given just this: move an order between New order,
    // Reorder and Remake, and fill the remake intake questionnaire. The optional
    // :orderNo segment lets the order detail deep-link straight into it.
    //
    // NOTE: `meta.permission` is only enforced where router/index.ts carries the
    // beforeEach guard. This branch has none, so the page checks the permission
    // itself as well — see OrderTypeChange.vue.
    path: '/order-type-change/:orderNo?',
    name: 'order-type-change',
    component: () => import('@/pages/OrderTypeChange.vue'),
    meta: { permission: 'change-order-type' },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: () => ({ top: 0 }),
  routes,
});

let isUserInitialized = false;

router.beforeEach(async (to, from, next) => {
  const store = useMainStore();
  const queryToken = to.query.access_token as string | undefined;
  setPageTitle();

  if (queryToken) {
    // Save token and remove from URL
    localStorage.setItem('access_token', queryToken);

    // A token handoff means a (possibly different) identity has just signed in,
    // so the cached user must be re-fetched. The redirect below is client-side,
    // which leaves this module — and therefore isUserInitialized — intact; with
    // the flag still true the guard would skip getCurrentUser() and keep the
    // PREVIOUS user's role while holding the NEW user's token. A merchant then
    // renders the staff dashboard shell (#19956). Logout never hit this because
    // it does a full window.location reload.
    isUserInitialized = false;

    // Impersonation context passed along by the legacy admin handoff.
    // A handoff WITHOUT the flag is a normal session and clears any old flag.
    if (String(to.query.impersonated || '') === '1') {
      saveImpersonation({
        by: {
          name: String(to.query.imp_by_name || ''),
          email: String(to.query.imp_by_email || ''),
          role: String(to.query.imp_by_role || ''),
        },
        started_at: String(to.query.imp_started_at || ''),
      });
    } else {
      clearImpersonation();
    }

    // Strip the token + impersonation params from the visible URL.
    const {
      access_token: _a,
      refresh_token: _r,
      impersonated: _imp,
      imp_by_name: _ibn,
      imp_by_email: _ibe,
      imp_by_role: _ibr,
      imp_started_at: _isa,
      ...cleanQuery
    } = to.query;

    // Redirect to same path without token in query
    return next({
      path: to.path,
      query: cleanQuery,
      replace: true,
    });
  }

  const token = localStorage.getItem('access_token');

  if (token && !isUserInitialized) {
    try {
      await store.getPermissions();
      await store.getNavigation();
      await store.getCurrentUser();

      isUserInitialized = true;

      if (!store.permissions.includes('access-new-dashboard')) {
        await store.logout();
        return;
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      await store.logout();
      return;
    }
  }

  // Per-route permission gate (task 18973): block direct-URL access to a
  // permission-restricted page (e.g. /tracking) when the user's role lacks the
  // required permission. Hidden sidebar entries alone don't stop a typed URL.
  const requiredPermission = to.meta.permission as string | undefined;
  if (requiredPermission && !can(requiredPermission)) {
    return next({ name: 'not-authorized' });
  }

  next();
});

export default router;
